import type { MouseEvent, ReactNode } from "react";
import type { Conversation } from "@zapir/shared";
import { Ack } from "./Bubble";
import { Avatar, avatarColors } from "./Avatar";
import { Icon } from "./Icon";
import { PinIndicator } from "./PinIndicator";
import { Tag } from "./Tag";
import { SwipeableRow, type SwipeAction } from "./SwipeableRow";
import { formatChatListTime } from "@/lib/format";
import { cx } from "@/lib/cx";

interface ConversationRowProps {
  conversation: Conversation;
  active?: boolean;
  typing?: boolean;
  muted?: boolean;
  onOpen: () => void;
  onMenu?: (event: MouseEvent) => void;
  onPin?: () => void;
  onToggleRead?: () => void;
}

/**
 * Linha da lista de conversas, no arranjo do WhatsApp: avatar 50px, titulo
 * 17px em peso normal, previa 15px com "Você:"/nome do remetente no mesmo
 * cinza; a hora sozinha em cima e silenciada/fixada/nao lidas juntos em
 * baixo. Acoes por swipe (fixar / lida).
 */
export function ConversationRow({
  conversation,
  active,
  typing,
  muted,
  onOpen,
  onMenu,
  onPin,
  onToggleRead,
}: ConversationRowProps) {
  const unread = conversation.unreadCount > 0;

  const chips: ReactNode[] = [];
  if (conversation.status !== "open") {
    chips.push(
      <span key="status" className="tag tag-soft">
        {statusLabel(conversation.status)}
      </span>,
    );
  }
  for (const label of conversation.labels) {
    chips.push(<Tag key={label.id} label={label.name} color={label.color} soft />);
  }
  if (conversation.assignee) {
    chips.push(
      <span key="assignee" className="assignee">
        @{conversation.assignee.name.split(" ")[0]}
      </span>,
    );
  }
  // ponytail: 1 chip + contador. Dois chips lado a lado nao cabem na linha da
  // previa e acabavam os dois com reticencias, sem informar nada.
  const shownChips = chips.slice(0, 1);
  const extraChips = chips.length - shownChips.length;

  // Prefixo da previa. Em enviada por nos vao os tiques, como no WhatsApp: um
  // glifo diz mais que "Você:" e ainda informa o ack. Em grupo vai o nome de
  // quem falou, no mesmo cinza do texto — em cor de destaque ele competia com
  // o badge e deixava a lista listrada.
  const senderPrefix = conversation.lastMessageOutgoing ? (
    conversation.lastMessageStatus ? (
      <span className="row-ack">
        <Ack level={conversation.lastMessageStatus} />
      </span>
    ) : (
      <span className="me">Você: </span>
    )
  ) : conversation.kind === "group" && conversation.lastMessageSenderName ? (
    <span className="me">{conversation.lastMessageSenderName}: </span>
  ) : null;

  const actions: SwipeAction[] = [];
  if (onToggleRead) {
    actions.push({
      id: "read",
      side: "left",
      tone: "primary",
      label: unread ? "Lida" : "Não lida",
      icon: <Icon name={unread ? "check" : "eye"} size={22} />,
      onAction: onToggleRead,
    });
  }
  if (onPin) {
    actions.push({
      id: "pin",
      side: "right",
      tone: "pinned",
      label: conversation.pinnedAt ? "Desafixar" : "Fixar",
      icon: <Icon name="pin" size={22} />,
      onAction: onPin,
    });
  }

  const row = (
    <div
      className={cx("row", active && "active")}
      role="listitem"
      tabIndex={0}
      aria-current={active ? "true" : undefined}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      {/* Selo da conexao de origem: cor e inicial deterministicas pelo nome do
          canal, com o nome inteiro na dica do hover. Nao rouba largura do
          nome/previa. */}
      <span className="row-avatar-wrap">
        <Avatar name={conversation.contactName} src={conversation.contactAvatarUrl} size={50} />
        <span
          className="row-channel-dot tip"
          role="img"
          aria-label={`Conexão: ${conversation.channelName}`}
          data-tip={conversation.channelName}
          style={{ background: avatarColors(conversation.channelName)[1] }}
        >
          {conversation.channelName.trim().charAt(0).toUpperCase()}
        </span>
      </span>
      <div className="row-body">
        <div className="row-top">
          <span className="row-title">{conversation.contactName}</span>
          <span className={cx("row-time", unread && "is-unread")}>
            {formatChatListTime(conversation.lastMessageAt)}
          </span>
        </div>
        <div className="row-bottom">
          {typing ? (
            <span className="row-preview font-medium" style={{ color: "var(--success-text)" }} aria-live="polite">
              digitando…
            </span>
          ) : (
            <span className="row-preview">
              {senderPrefix}
              {conversation.lastMessagePreview ?? "Sem mensagens"}
            </span>
          )}
          {shownChips.length > 0 ? (
            <span className="row-labels">
              {shownChips}
              {extraChips > 0 ? <span className="tag tag-soft tag-count">+{extraChips}</span> : null}
            </span>
          ) : null}
          {/* Canto inferior direito, como no WhatsApp: silenciada, fixada e nao
              lidas juntos — a hora fica sozinha em cima. */}
          <span className="row-right">
            {muted ? (
              <span className="row-icons">
                <Icon name="mute" size={16} label="Silenciada" />
              </span>
            ) : null}
            {conversation.pinnedAt ? <PinIndicator /> : null}
            {unread ? (
              <span className="pill-badge">
                {conversation.unreadCount > 99 ? "99+" : conversation.unreadCount}
                <span className="sr-only"> não lidas</span>
              </span>
            ) : null}
          </span>
        </div>
      </div>
      {onMenu ? (
        <button
          type="button"
          className="row-menu"
          aria-label={`Mais ações da conversa com ${conversation.contactName}`}
          onClick={(event) => {
            event.stopPropagation();
            onMenu(event);
          }}
        >
          <Icon name="more" size={20} />
        </button>
      ) : null}
    </div>
  );

  if (actions.length === 0) return row;
  return <SwipeableRow actions={actions}>{row}</SwipeableRow>;
}

function statusLabel(status: Conversation["status"]): string {
  switch (status) {
    case "pending":
      return "pendente";
    case "snoozed":
      return "adiada";
    case "closed":
      return "fechada";
    default:
      return "aberta";
  }
}
